import { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, X, Image, Video, FileText } from 'lucide-react';

interface FileUploaderProps {
  onFilesSelected: (files: { images: File[]; videos: File[]; setup_files: File[] }) => void;
  selectedFiles: { images: File[]; videos: File[]; setup_files: File[] };
  onRemoveFile: (type: 'images' | 'videos' | 'setup_files', index: number) => void;
  isUploading?: boolean;
  uploadProgress?: number;
}

const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_VIDEO_SIZE = 500 * 1024 * 1024; // 500MB
const MAX_SETUP_SIZE = 5 * 1024 * 1024; // 5MB

const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
const VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];

export function FileUploader({
  onFilesSelected,
  selectedFiles,
  onRemoveFile,
  isUploading = false,
  uploadProgress,
}: FileUploaderProps) {
  const onDrop = useCallback((acceptedFiles: File[]) => {
    const images: File[] = [];
    const videos: File[] = [];
    const setup_files: File[] = [];

    acceptedFiles.forEach((file) => {
      if (IMAGE_TYPES.includes(file.type)) {
        if (file.size <= MAX_IMAGE_SIZE) {
          images.push(file);
        }
      } else if (VIDEO_TYPES.includes(file.type)) {
        if (file.size <= MAX_VIDEO_SIZE) {
          videos.push(file);
        }
      } else {
        if (file.size <= MAX_SETUP_SIZE) {
          setup_files.push(file);
        }
      }
    });

    onFilesSelected({
      images: [...selectedFiles.images, ...images],
      videos: [...selectedFiles.videos, ...videos],
      setup_files: [...selectedFiles.setup_files, ...setup_files],
    });
  }, [selectedFiles, onFilesSelected]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled: isUploading,
  });

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const totalFiles = selectedFiles.images.length + selectedFiles.videos.length + selectedFiles.setup_files.length;

  return (
    <div className="space-y-4">
      {/* Dropzone */}
      <div
        {...getRootProps()}
        className={`
          border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
          ${isDragActive ? 'border-primary bg-primary/5' : 'border-gray-300 hover:border-primary/50'}
          ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input {...getInputProps()} />
        <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        {isDragActive ? (
          <p className="text-lg font-medium">Drop files here...</p>
        ) : (
          <div>
            <p className="text-lg font-medium mb-2">
              Drag & drop files here, or click to select
            </p>
            <p className="text-sm text-gray-500">
              Images (PNG, JPG, GIF, WebP - max 10MB) •
              Videos (MP4, WebM, MOV - max 500MB) •
              Setup files (max 5MB)
            </p>
          </div>
        )}
      </div>

      {/* Upload Progress */}
      {isUploading && uploadProgress !== undefined && (
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div
            className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
            style={{ width: `${uploadProgress}%` }}
          ></div>
        </div>
      )}

      {/* Selected Files Preview */}
      {totalFiles > 0 && (
        <div className="space-y-3">
          <h4 className="font-medium text-sm">
            Selected Files ({totalFiles})
          </h4>

          {/* Images */}
          {selectedFiles.images.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Image className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium">Images ({selectedFiles.images.length})</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {selectedFiles.images.map((file, index) => (
                  <div key={index} className="relative group">
                    <div className="aspect-square rounded-lg border bg-gray-50 flex flex-col items-center justify-center p-2">
                      <Image className="h-8 w-8 text-gray-400 mb-1" />
                      <p className="text-xs text-center truncate w-full">{file.name}</p>
                      <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                    </div>
                    <button
                      onClick={() => onRemoveFile('images', index)}
                      disabled={isUploading}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Videos */}
          {selectedFiles.videos.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Video className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium">Videos ({selectedFiles.videos.length})</span>
              </div>
              <div className="space-y-2">
                {selectedFiles.videos.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
                    <div className="flex items-center gap-3">
                      <Video className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium">{file.name}</p>
                        <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => onRemoveFile('videos', index)}
                      disabled={isUploading}
                      className="text-red-500 hover:text-red-700 disabled:opacity-50"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Setup Files */}
          {selectedFiles.setup_files.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <FileText className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium">Setup Files ({selectedFiles.setup_files.length})</span>
              </div>
              <div className="space-y-2">
                {selectedFiles.setup_files.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium">{file.name}</p>
                        <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => onRemoveFile('setup_files', index)}
                      disabled={isUploading}
                      className="text-red-500 hover:text-red-700 disabled:opacity-50"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
